<template>
  <span>
    <confirm-modal
      id="previous-document-type-confirm"
      :v-if="confirmPreviousDocument"
      title="Are you sure you want to proceed?"
      :confirmation="prevDocTypeConfirmText"
      @submit="submit()"
    />
    <b-modal
      :id="this.$attrs.id"
      v-model="isVisible"
      title="Declaration information"
      size="lg"
    >
      <b-form>
        <validation-observer ref="formValidation">
          <b-form-group
            v-if="!clearance"
            label="Declaration type"
            label-for="arrived"
            label-class="mb-1"
          >
            <validation-provider
              #default="{ errors }"
              name="Declaration type"
              rules="required"
            >
              <b-form-radio-group
                id="arrived"
                v-model="hasArrived"
                :options="arrivedOptions"
                name="arrived"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            label="Choose warehouse"
            label-for="vue-select"
          >
            <validation-provider
              #default="{ errors }"
              name="Warehouse"
              :rules="'required'"
            >
              <b-form-select
                id="warehouse"
                v-model="warehouse"
                :options="warehouses"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="shipment.entry_country === 'RO'"
            label="Previous document category"
            label-for="previous_document_category"
          >
            <validation-provider
              #default="{ errors }"
              name="Previous document category"
            >
              <b-form-select
                id="previous_document_category"
                v-model="previous_document_category"
                :options="previous_document_categories"
                placeholder="Choose document category"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="shipment.entry_country !== 'LT'"
            label="Previous document type"
            label-for="previous_document_type-input"
          >
            <validation-provider
              #default="{ errors }"
              name="Previous document type"
              :rules="previousDocumentRules"
            >
              <classifier-select
                id="previous_document_type"
                v-model="previous_document_type"
                :classifier-key="classifierKeys.previousDocumentTypes"
                :country="previousDocumentCountry"
                :empty-code-selectable="previousDocumentCountry === 'NL'"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="shouldShowPreviousDocRef"
            label="Previous document reference"
            label-for="previous_document_reference"
          >
            <validation-provider
              #default="{ errors }"
              name="Previous document reference"
              :rules="previousDocumentRules"
            >
              <b-form-input
                id="previous_document_reference"
                v-model="previous_document_reference"
                placeholder="Previous document reference"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-alert
            v-if="isFinnishH1"
            show
            variant="info"
            class="mb-1"
          >
            <div class="alert-body">
              <p class="mb-50">
                Finnish Customs (Tulli) requires a previous document on standard declarations —
                submitting without one is rejected (NC0022). If there is no real preceding
                declaration, select <strong>1ZZZ — Other previous document</strong> and enter any
                free-text reference.
              </p>
              <p class="mb-0">
                <strong>NMRN — Declaration/notification MRN</strong> is state-verified against
                Tulli's records: use it only with a real Finnish MRN known to Tulli — a fake or
                unknown MRN is rejected (NR1302).
              </p>
            </div>
          </b-alert>
          <b-form-group
            v-if="previousDocumentRequireNumberOfItemsMap[previous_document_type]"
            label="Previous document number of items"
            label-for="number_of_items"
          >
            <validation-provider
              #default="{ errors }"
              name="Previous document number of items"
              :rules="{ between: { min: 1, max: 99 }}"
            >
              <sup class="text-danger">{{ errors[0] }}</sup>
              <b-form-input
                id="previous_document_number_of_items"
                v-model.number="previous_document_number_of_items"
                placeholder="Previous document number of items"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="!resendables"
            label-for="apply_same_transport_information"
          >
            <b-form-checkbox
              id="apply_same_transport_information"
              v-model="apply_same_transport_information"
              name="apply_same_transport_information"
              :value="true"
              :unchecked-value="false"
            >
              Apply same transport information for all declarations
            </b-form-checkbox>
          </b-form-group>
          <div
            v-if="!resendables && apply_same_transport_information && transportDataLoading"
            class="text-center my-2"
          >
            <b-spinner small />
            <span class="ml-1">Loading transport information...</span>
          </div>
          <b-alert
            :show="!resendables && apply_same_transport_information && !transportDataLoading && transportDataDivergent"
            variant="warning"
            class="mb-1"
          >
            <div class="alert-body">
              The selected parcels have different transport information. The values below are taken from one of them and will be applied to all declarations. Please review before submitting.
            </div>
          </b-alert>
          <b-form-group
            v-if="!resendables && apply_same_transport_information && !transportDataLoading"
            label="Transport mode at border"
            label-for="transport_mode_at_border"
          >
            <validation-provider
              #default="{ errors }"
              name="Transport mode at border"
              rules="required"
            >
              <classifier-select
                id="transport_mode_at_border"
                v-model="transport_mode_at_border"
                :classifier-key="classifierKeys.transportModes"
                :country="classifierCountry"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="!resendables && apply_same_transport_information"
            label="Transport mode inland"
            label-for="transport_mode_inland"
          >
            <validation-provider
              #default="{ errors }"
              name="Transport mode inland"
              rules="required"
            >
              <classifier-select
                id="transport_mode_inland"
                v-model="transport_mode_inland"
                :classifier-key="classifierKeys.transportModes"
                :country="classifierCountry"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="!resendables && apply_same_transport_information"
            label="Transport arrival means identification type"
            label-for="transport_arrival_means_identification_type"
          >
            <validation-provider
              #default="{ errors }"
              name="Transport arrival means identification type"
            >
              <classifier-select
                id="transport_arrival_means_identification_type"
                v-model="transport_arrival_means_identification_type"
                :classifier-key="classifierKeys.transportIdentificationTypes"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="!resendables && apply_same_transport_information"
            label="Transport arrival means identification"
            label-for="transport_arrival_means_identification"
          >
            <validation-provider
              #default="{ errors }"
              name="Transport arrival means identification"
              rules="max:35"
            >
              <b-form-input
                id="transport_arrival_means_identification"
                v-model="transport_arrival_means_identification"
                placeholder="Transport arrival means identification"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="!resendables && apply_same_transport_information"
            label="Transport means registration nationality at border"
            label-for="transport_means_registration_nationality_at_border"
          >
            <validation-provider
              #default="{ errors }"
              name="Transport means registration nationality at border"
            >
              <classifier-select
                id="transport_means_registration_nationality_at_border"
                v-model="transport_means_registration_nationality_at_border"
                :classifier-key="classifierKeys.countries"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="showNotArrivedOption && warehouse && !resendables && shipment.company.config && shipment.company.config.arrivedToFacilityEventSelected"
            label="Some of selected containers/parcels are missing 'Arrived to facility' event"
            label-class="text-warning"
            label-for="send_arrived_to_facility"
          >
            <b-form-checkbox
              id="send_arrived_to_facility"
              v-model="send_arrived_to_facility"
              name="send_arrived_to_facility"
              :value="true"
              :unchecked-value="false"
            >
              Send "Arrived to facility" event
            </b-form-checkbox>
          </b-form-group>
          <b-form-group
            v-if="noaEnabled && !resendables"
            label="Notification of Arrival (NOA) Date/Time"
            label-for="noa_at"
          >
            <validation-provider
              #default="{ errors }"
              name="NOA date and time"
            >
              <b-input-group>
                <flat-pickr
                  id="noa_at"
                  ref="noaDatePicker"
                  v-model="noa_at"
                  :config="noaConfig"
                  class="form-control"
                  placeholder="Enter date and time YYYY-MM-DD HH:mm"
                  :state="errors.length > 0 ? false : null"
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    @click="$refs.noaDatePicker.fp.toggle()"
                  >
                    <feather-icon icon="CalendarIcon" />
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    @click="noa_at = null"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </validation-observer>
      </b-form>

      <template #modal-footer="">
        <b-button
          variant="primary"
          :disabled="!shipment.entry_country || (shipment.entry_country === 'NL' && !previous_document_type && !isNoPreviousDocumentSelected)"
          @click="validateSend()"
        >
          Submit declarations
        </b-button>
      </template>
    </b-modal>
  </span>
</template>

<script>

import {
  BAlert,
  BButton,
  BForm,
  BFormGroup,
  BFormRadioGroup,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BModal,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import handleError from '@/views/components/errorHandler'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'
import flatPickr from 'vue-flatpickr-component'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import {
  CUSTOMS_CLASSIFIER_KEYS,
  PREVIOUS_DOCUMENT_CATEGORIES,
  classifierCountryParam,
  formatClassifierOption,
  getPreviousDocumentTypes,
} from '@/libs/customsClassifiers'
import 'flatpickr/dist/flatpickr.css'

// Send modal for H1 declarations. Split from SubmitDeclarationsModal (H7/ENS):
// FI gets a Tulli hint, and the transport section can apply one transport info
// set to all declarations. Every code list here, previous document types
// included, comes from the shared classifier client on its usual expiry.
export default {
  components: {
    ClassifierSelect,
    ConfirmModal,
    ValidationProvider,
    ValidationObserver,
    BAlert,
    BButton,
    BForm,
    BFormGroup,
    BFormRadioGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BModal,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    flatPickr,
  },
  inheritAttrs: false,
  props: {
    clearance: {
      type: Boolean,
      default: false,
    },
    resendables: {
      type: Boolean,
      default: false,
    },
    shipment: {
      type: Object,
      required: true,
    },
    selectedParcels: {
      type: Array,
      required: true,
    },
    selectedContainers: {
      type: Array,
      required: true,
    },
    selectedAll: {
      type: Boolean,
      default: false,
    },
    allParcelsData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      arrivedOptions: [
        { text: 'A - Regular', value: true },
        { text: 'D - Pre-arrival', value: false },
      ],
      showNotArrivedOption: false,
      isVisible: false,
      // Canonical classifier options: { code, label, parentCode, attributes, flags }.
      // Loaded from the shared classifier client, which caches them, so this
      // costs no request beyond the one the dropdown below already makes.
      previousDocumentTypes: [],
      confirmPreviousDocument: false,
      prevDocTypeConfirmText: '',
      hasArrived: null,
      warehouse: '',
      warehouses: [{ value: '', text: '----select----' }],
      previous_document_category: '',
      previous_document_type: null,
      previous_document_number_of_items: null,
      previous_document_reference: '',
      apply_same_transport_information: false,
      transport_mode_at_border: null,
      transport_mode_inland: null,
      transport_arrival_means_identification_type: null,
      transport_arrival_means_identification: '',
      transport_means_registration_nationality_at_border: null,
      transportDataLoading: false,
      transportDataDivergent: false,
      classifierKeys: CUSTOMS_CLASSIFIER_KEYS,
      send_arrived_to_facility: this.shipment.company.config?.arrivedToFacilityEventSelected ?? false,
      noa_at: null,
    }
  },
  computed: {
    // Transport-mode validation is country-aware on the backend, so the selects
    // pass the entry country and let the backend select its snapshot.
    classifierCountry() {
      return classifierCountryParam(this.shipment.entry_country)
    },
    // Every country resolves its own previous document type list; the backend
    // serves the country overlay or the curated list for it.
    previousDocumentCountry() {
      return classifierCountryParam(this.shipment.entry_country)
    },
    // The shared X/Y/Z constant, rendered by the shared formatter so the
    // category reads as code plus description like every classifier list.
    previous_document_categories() {
      return [{ value: '', text: '----select----' }]
        .concat(PREVIOUS_DOCUMENT_CATEGORIES.map(option => ({
          value: option.code,
          text: formatClassifierOption(option),
        })))
    },
    defaultTransportMode() {
      return this.shipment?.company?.config?.defaultTransportMode ?? null
    },
    defaultTransportRegistrationNumber() {
      return this.shipment?.company?.config?.defaultTransportRegistrationNumber ?? ''
    },
    isFinnishH1() {
      return this.shipment.entry_country === 'FI'
    },
    // Finnish Customs (Tulli) still requires a previous document on standard H1
    // declarations (omitting it is rejected with NC0022), but the backend accepts
    // it as nullable for FI H1 — so the field stays visible without a hard
    // client-side requirement and the FI hint above guides the user instead.
    previousDocumentRules() {
      if (this.shipment.entry_country === 'RO' || this.shipment.entry_country === 'FI') {
        return ''
      }
      // NL's empty-code entry is a valid answer, but 'required' rejects an empty
      // string, so it would block sending. The resend dialog relaxes it the same way.
      if (this.isNoPreviousDocumentSelected) {
        return ''
      }
      return 'required'
    },
    noaEnabled() {
      return this.shipment?.company?.config?.enableNOA === true
    },
    noaConfig() {
      return {
        enableTime: true,
        time_24hr: true,
        altInput: true,
        altFormat: 'Y-m-d H:i',
        dateFormat: 'Y-m-d H:i',
        maxDate: new Date(),
        allowInput: true,
        clickOpens: false,
      }
    },
    shouldShowPreviousDocRef() {
      if (this.shipment.entry_country === 'LT') {
        return false
      }
      return !(this.shipment.entry_country === 'NL' && this.isNoPreviousDocumentSelected)
    },
    isNoPreviousDocumentSelected() {
      if (this.shipment.entry_country !== 'NL') {
        return false
      }
      // NL's "no previous document" entry is the option whose code is an empty
      // string. null means nothing has been selected yet, so it does not count.
      return this.previous_document_type === ''
    },
    previousDocumentRequireNumberOfItemsMap() {
      return this.previousDocumentTypes.reduce((map, option) => {
        map[option.code] = option.flags?.require_number_of_items ?? false
        return map
      }, {})
    },
  },
  watch: {
    selectedParcels() {
      this.handleSendH7Declarations()
    },
    selectedContainers() {
      this.handleSendH7Declarations()
    },
    selectedAll() {
      this.handleSendH7Declarations()
    },
    previousDocumentCountry() {
      this.previousDocumentTypes = []
      this.loadPreviousDocumentTypes()
    },
    previous_document_type(newVal) {
      this.loadPreviousDocumentTypes()
      // Selecting NL's empty-code entry discards a reference typed before it,
      // so a stale value is never submitted with the declaration.
      if (this.shipment.entry_country === 'NL' && newVal === '') {
        this.previous_document_reference = null
      }
      if (!this.previousDocumentRequireNumberOfItemsMap[newVal]) {
        this.previous_document_number_of_items = null
      }
    },
    apply_same_transport_information(newVal) {
      if (newVal) {
        this.prefillTransportData()
      } else {
        this.transport_mode_at_border = null
        this.transport_mode_inland = null
        this.transport_arrival_means_identification_type = null
        this.transport_arrival_means_identification = ''
        this.transport_means_registration_nationality_at_border = null
      }
    },
  },
  created() {
    this.$http.get(`/v1/companies/${this.shipment.company_id}/warehouses`)
      .then(response => {
        const warehouses = response.data.data.filter(x => x.country === this.shipment.entry_country)
        const moreThan1Company = warehouses.length > 0
          ? (new Set(warehouses.map(warehouse => warehouse?.company?.id))).size > 1
          : false

        this.warehouses = this.warehouses.concat(warehouses.map(warehouse => ({
          value: warehouse.id,
          text: moreThan1Company ? `${warehouse.name} (${warehouse.company.name})` : warehouse.name,
        })))
      })
    this.handleSendH7Declarations()
  },
  methods: {
    // The flags (confirm, require_number_of_items) that drive the rules above.
    // Loaded only once a type is selected, which keeps this modal as lazy as the
    // dropdown beside it: selecting a type means that dropdown has already
    // loaded the list, so this resolves from the shared cache without a second
    // request. A failure is not reported here either — the dropdown owns the
    // error state and its Retry, and reloads this list along with its own.
    loadPreviousDocumentTypes() {
      if (this.previous_document_type === null || this.previous_document_type === undefined) {
        return
      }
      const country = this.previousDocumentCountry
      getPreviousDocumentTypes(country)
        .then(options => {
          if (this.previousDocumentCountry === country) this.previousDocumentTypes = options
        })
        .catch(() => {
          if (this.previousDocumentCountry === country) this.previousDocumentTypes = []
        })
    },
    handleSendH7Declarations() {
      this.$http.post(
        `/v1/companies/${this.shipment.company_id}/shipments/${this.shipment.id}/parcels-arrived`,
        this.getSelected(),
      )
        .then(response => {
          this.showNotArrivedOption = !!response.data.hasMissingEvent
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    getSelected() {
      const ret = {
        all: null,
        containers: null,
        goods_shipments: null,
      }
      if (this.selectedAll) {
        // send only all if it is selected
        ret.all = true
      } else {
        // send selected containers and separately selected parcels
        ret.containers = this.selectedContainers
        ret.goods_shipments = this.selectedParcels
        if (ret.containers.length) {
          // remove parcels where container is selected
          const parcelIds = this.getSelectedContainersParcels()
          ret.goods_shipments = ret.goods_shipments.filter(x => !parcelIds.includes(x))
        }
      }
      return ret
    },
    getSelectedContainersParcels() {
      if (!this.selectedParcels || !this.selectedParcels.length || !Array.isArray(this.allParcelsData)) {
        return []
      }
      const containerCodesSet = new Set(this.selectedContainers)
      return (this.allParcelsData || [])
        .map(parcelGroup => Object.values(parcelGroup || {}).flat()
          .filter(parcel => parcel && containerCodesSet.has(parcel.container_code))
          .map(parcel => parcel.id))
        .flat()
    },
    transportDataSignature(transportData) {
      if (!transportData) {
        return ''
      }
      return [
        transportData.border_transport_means_mode,
        transportData.arrival_transport_means_mode,
        transportData.arrival_transport_means_identification_type,
        transportData.arrival_transport_means_identification,
        transportData.border_transport_means_registration_nationality,
      ].map(value => value ?? '').join('|')
    },
    // Returns the transport_data of the selected parcels/containers and flags
    // whether the selected parcels disagree (divergent transport data).
    resolveTransportDataFromList(transportDataList) {
      const filtered = (transportDataList || []).filter(Boolean)
      if (!filtered.length) {
        this.transportDataDivergent = false
        return null
      }
      const signatures = new Set(filtered.map(item => this.transportDataSignature(item)))
      this.transportDataDivergent = signatures.size > 1
      return filtered[0]
    },
    findManifestTransportData() {
      if (!Array.isArray(this.allParcelsData) || !this.allParcelsData.length) {
        this.transportDataDivergent = false
        return null
      }
      const allParcels = this.allParcelsData
        .map(parcelGroup => Object.values(parcelGroup || {}).flat())
        .flat()
        .filter(item => item && item.transport_data)
      if (!allParcels.length) {
        this.transportDataDivergent = false
        return null
      }
      if (this.selectedAll) {
        return this.resolveTransportDataFromList(allParcels.map(item => item.transport_data))
      }
      if (this.selectedParcels && this.selectedParcels.length) {
        // selectedParcels holds tracking numbers, match tracking_number (fallback to id)
        const selectedParcelsSet = new Set(this.selectedParcels)
        const matched = allParcels.filter(item => selectedParcelsSet.has(item.tracking_number) || selectedParcelsSet.has(item.id))
        if (matched.length) {
          return this.resolveTransportDataFromList(matched.map(item => item.transport_data))
        }
      }
      if (this.selectedContainers && this.selectedContainers.length) {
        // Take the selected containers' data explicitly rather than the first
        // arbitrary parcel that happens to carry transport_data.
        const containerCodesSet = new Set(this.selectedContainers)
        const matched = allParcels.filter(item => containerCodesSet.has(item.container_code))
        if (matched.length) {
          return this.resolveTransportDataFromList(matched.map(item => item.transport_data))
        }
      }
      this.transportDataDivergent = false
      return null
    },
    applyTransportData(transportData) {
      this.transport_mode_at_border = transportData?.border_transport_means_mode ?? this.defaultTransportMode
      this.transport_mode_inland = transportData?.arrival_transport_means_mode ?? this.defaultTransportMode
      const manifestIdentification = transportData?.arrival_transport_means_identification || ''
      const manifestIdentificationType = transportData?.arrival_transport_means_identification_type || null
      this.transport_arrival_means_identification = manifestIdentification || this.defaultTransportRegistrationNumber
      // The company-settings default carries no identification type. Air on both legs is the one
      // unambiguous case (41 - aircraft registration); it mirrors the IE415H1 fallback so the modal
      // never prefills a type the backend would not have chosen itself.
      const isAir = Number(this.transport_mode_inland) === 4 && Number(this.transport_mode_at_border) === 4
      const isCompanyDefaultIdentification = !manifestIdentification && !!this.transport_arrival_means_identification
      this.transport_arrival_means_identification_type = manifestIdentificationType
        ?? (isCompanyDefaultIdentification && isAir ? '41' : null)
      this.transport_means_registration_nationality_at_border = transportData?.border_transport_means_registration_nationality ?? null
    },
    prefillTransportData() {
      this.transportDataDivergent = false
      // Prefer data already loaded in the tree (expanded containers)...
      const manifestTransportData = this.findManifestTransportData()
      if (manifestTransportData) {
        this.applyTransportData(manifestTransportData)
        return
      }
      // ...otherwise fetch it from the backend so the prefill does not depend on
      // whether the user expanded a container in the tree.
      this.transportDataLoading = true
      this.fetchManifestTransportData()
        .then(transportData => this.applyTransportData(transportData))
        .catch(() => this.applyTransportData(null))
        .finally(() => {
          this.transportDataLoading = false
        })
    },
    fetchManifestTransportData() {
      const containerCode = (this.selectedContainers && this.selectedContainers.length)
        ? this.selectedContainers[0]
        : null
      if (!containerCode) {
        return Promise.resolve(null)
      }
      const url = `/v1/shipments/${this.shipment.id}/h1/containers/${containerCode}/goods-shipments`
      return this.$http.get(url, {
        params: {
          filter: this.resendables ? 'resendables' : 'notSent',
        },
      })
        .then(response => {
          const parcels = response?.data?.data || []
          const transportDataList = parcels
            .filter(item => item && item.transport_data)
            .map(item => item.transport_data)
          return this.resolveTransportDataFromList(transportDataList)
        })
    },
    validateSend() {
      this.$refs.formValidation.validate().then(success => {
        if (!success) {
          return
        }
        const selectedDocumentType = this.previousDocumentTypes
          .find(option => option.code === this.previous_document_type)
        if (selectedDocumentType?.flags?.confirm) {
          const { label } = selectedDocumentType
          this.confirmPreviousDocument = true
          this.$bvModal.show('previous-document-type-confirm')
          this.prevDocTypeConfirmText = `You selected "${label}", are you sure you want to continue`
        } else {
          this.submit()
        }
      })
    },
    // Builds the nested transport_data object expected by the H1 declarations
    // endpoint. Empty values are omitted so transport_data never overrides the
    // legacy-field defaults with blanks (backend: transport_data wins per key).
    buildTransportData() {
      const transportData = {
        border_transport_means_mode: this.transport_mode_at_border,
        arrival_transport_means_mode: this.transport_mode_inland,
        arrival_transport_means_identification_type: this.transport_arrival_means_identification_type,
        arrival_transport_means_identification: this.transport_arrival_means_identification,
        border_transport_means_registration_nationality: this.transport_means_registration_nationality_at_border,
      }
      return Object.keys(transportData).reduce((acc, key) => {
        const value = transportData[key]
        if (value !== null && value !== undefined && value !== '') {
          acc[key] = value
        }
        return acc
      }, {})
    },
    submit() {
      this.$bvModal.hide('previous-document-type-confirm')
      this.$emit('submit', {
        previous_document_type: this.previous_document_type,
        previous_document_category: this.previous_document_category,
        previous_document_reference: this.previous_document_reference,
        send_arrived_to_facility: this.send_arrived_to_facility,
        warehouse_id: this.warehouse,
        has_arrived: this.hasArrived,
        inland_mode_of_transport: this.transport_mode_inland || this.defaultTransportMode,
        mode_of_transport_at_border: this.transport_mode_at_border || this.defaultTransportMode,
        transport_registration_number: this.transport_arrival_means_identification || this.defaultTransportRegistrationNumber,
        previous_document_number_of_items: this.previous_document_number_of_items,
        noa_at: this.noa_at,
        ...(this.apply_same_transport_information ? {
          transport_data: this.buildTransportData(),
        } : {}),
      })
      this.previous_document_type = null
      this.previous_document_category = ''
      this.previous_document_reference = null
      this.warehouse = ''
      this.isVisible = false
      this.previous_document_number_of_items = null
      this.noa_at = null
      this.apply_same_transport_information = false
      this.transport_mode_at_border = null
      this.transport_mode_inland = null
      this.transport_arrival_means_identification_type = null
      this.transport_arrival_means_identification = ''
      this.transport_means_registration_nationality_at_border = null
    },
  },
}
</script>

<style scoped>

</style>
